/**
 * FTR6 HUD overlay. CSS/2D over the WebGL canvas, driven by sim state.
 * Renders exactly the elements in creative brief §3:
 *  - throttle bar + numeric velocity (auto km/s)        [bottom-center]
 *  - flight-assist pill ASSIST ON/OFF                   [bottom-left]
 *  - target name + distance + closing speed             [top-center]
 *  - center reticle + velocity-vector (prograde) marker [center, SVG]
 *  - edge-clamped target bracket + off-screen arrow     [SVG]
 *  - supercruise mode + spool meter + SAFE DISENGAGE    [top-right]
 *  - 3D radar overlay frame                              [bottom-right]
 */
import * as THREE from 'three';
import './hud.css';
import { fmtSpeed, fmtDistance, fmtClosing } from './format.js';
import type { TargetInfo } from '../travel/target.js';
import type { CruiseState } from '../travel/supercruise.js';
import type { DockingPhase, VerifyStatus } from '@blackmarble/sim-core';
import type { CameraMode } from '../render/cameras.js';

export interface HudFrame {
  throttle: number; // -1..1
  speedKmS: number;
  assist: boolean;
  rotAssist: boolean; // FLIGHT-FEEL A2: rotational assist (separate from linear)
  // FLIGHT-FEEL A4: power-distribution pips (each 0..PIP_MAX).
  pips: { sys: number; eng: number; wep: number };
  // FLIGHT-FEEL A5: throttle blue-zone band (fractions 0..1 of max) + in-zone flag.
  blueLo: number;
  blueHi: number;
  inBlueZone: boolean;
  target: TargetInfo | null;
  targetScreen: { x: number; y: number; behind: boolean } | null;
  progradeScreen: { x: number; y: number } | null;
  cruise: CruiseState;
  spoolPercent: number;
  canSafeDisengage: boolean;
  // normalised body-frame radar contacts: x = right/left, y = height, z = forward/back.
  radarBlips: {
    x: number;
    y: number;
    z: number;
    kind: 'hostile' | 'neutral' | 'signal' | 'body';
    selected: boolean;
    threat?: number;
  }[];
  // M2 P1: ship identity + state (fuel/hull/shield/energy as 0..1 fractions).
  shipName: string;
  hull01: number;
  shield01: number;
  fuel01: number;
  energy01: number;
  // M2 P2: docking. dockStationName null => no station in range (panel hidden).
  dockPhase: DockingPhase;
  dockStationName: string | null;
  dockDistanceKm: number;
  dockSpeedKmS: number;
  dockPromptText: string; // pre-baked contextual line (built in main.ts)
  // M2 P4: combat. combatName null => no lock (panel hidden).
  combatName: string | null;
  combatHull01: number;
  combatShield01: number;
  combatDistanceKm: number;
  // FIX 3: brief flash cues — >0 means the flash is active this frame.
  combatHitFlash: number;  // seconds remaining for outgoing hit confirmation
  combatDmgFlash: number;  // seconds remaining for incoming damage cue
  // M2 P4: the Roll — quietly named recently-lost ships (most recent first).
  rollNotices: { name: string; age: number }[];
  // M2 P5: economy.
  credits: number;
  cargoUsed: number;
  cargoCapacity: number;
  missionText: string | null; // active-mission line, null => hidden
  // M3 WORLD B3: signal-source readout. signalLabel null => no signal in range.
  signalLabel: string | null; // e.g. 'DISTRESS CALL · THREAT 3'
  signalDistanceKm: number;
  signalThreat: number; // 0..5
  signalPrompt: string; // 'DROP [X]' when droppable, '' otherwise
  scoopPrompt: string; // 'SCOOP [T]' when a canister is in range, '' otherwise
  verifyPrompt: string; // AAA-03: 'VERIFY [V]' at a verifiable signal, '' otherwise
  verifyStatus: VerifyStatus | null;
  verifyGuidance: string | null; // FIX 3: terse operator guidance / result gloss
  // M3 WORLD B6: active objective line (null => hidden). B-NARRATIVE drift-log.
  objectiveText: string | null;
  driftLog: string | null; // readable derelict log on drop-in, null => hidden
  // AAA-02: Verification Ledger rows — carved names revealed glyph-by-glyph.
  ledger: { name: string; status: VerifyStatus; revealChars: number }[];
  // ITEM 3: orbital-lines overlay toggle indicator.
  orbitLinesOn: boolean;
  // H3 COMBAT READABILITY
  /** Locked-target screen position + HP for the bracket-overlay health bar. */
  combatLockScreen: { x: number; y: number; hull01: number; shield01: number } | null;
  /** Off-screen enc-* hostile directions (capped at 5) — for edge-clamped arrows. */
  combatOffScreen: { angle: number }[];
  /** Projected intercept point (lead pip) for the locked target. Null when no lock/solution. */
  combatLeadScreen: { x: number; y: number } | null;
  /** Direction (normalised screen delta) from ship toward most-recent attacker. Fades out. */
  combatDmgDir: { angle: number; fade: number } | null;
  // M4 CONTROL-FEEL: virtual-stick deflection (x = yaw, y = pitch, each -1..1).
  // Shows commanded turn vs actual nose. Null hides it (docked / menus open).
  stick: { x: number; y: number } | null;
}

const NS = 'http://www.w3.org/2000/svg';
/** The Roll fades slowly over its hold so a death lingers (non-skippable). */
const ROLL_HOLD_SEC = 8;

export class Hud {
  private readonly root: HTMLDivElement;
  private readonly instrumentLayer: HTMLDivElement;
  private readonly scanlineEl: HTMLDivElement;
  private readonly throttleFill: HTMLElement;
  private readonly throttleBlue: HTMLElement;
  private readonly speedEl: HTMLElement;
  private readonly assistEl: HTMLElement;
  // FLIGHT-FEEL A4: pip cells per subsystem.
  private readonly pipCells: Record<'sys' | 'eng' | 'wep', HTMLElement>;
  private readonly targetEl: HTMLElement;
  private readonly cruiseModeEl: HTMLElement;
  private readonly spoolFill: HTMLElement;
  private readonly safeEl: HTMLElement;
  private readonly svg: SVGSVGElement;
  private readonly bracket: SVGRectElement;
  private readonly arrow: SVGPolygonElement;
  private readonly prograde: SVGCircleElement;
  private readonly radar: HTMLDivElement;
  // FIX 3: combat lock reticle — corner brackets tracking the locked target.
  private readonly lockReticle: SVGGElement;
  // H3: combat readability — pooled SVG elements (no per-frame allocation).
  private readonly lockHealthBar: SVGGElement;       // hull+shield bars at the bracket
  private readonly leadPip: SVGCircleElement;        // aim-intercept indicator
  private readonly dmgDirArrow: SVGPolygonElement;   // directional damage indicator
  private readonly hitMarker: SVGGElement;
  // M4 CONTROL-FEEL: virtual-stick indicator (guide ring + deflection dot).
  private readonly stickGroup: SVGGElement;
  private readonly stickDot: SVGCircleElement;
  private readonly offScreenPool: SVGPolygonElement[]; // edge-clamped hostile arrows (pool of 5)
  // M2 P1: ship-state panel (top-right, under cruise) — fuel/hull/shield/energy.
  private readonly shipNameEl: HTMLElement;
  private readonly hullBar: HTMLElement;
  private readonly shieldBar: HTMLElement;
  private readonly fuelBar: HTMLElement;
  private readonly energyBar: HTMLElement;
  // M2 P2: docking guidance panel (bottom-center, above throttle).
  private readonly dockEl: HTMLElement;
  // M2 P4: locked-target HP panel + the Roll death notices.
  private readonly combatEl: HTMLElement;
  private readonly rollEl: HTMLElement;
  private readonly econEl: HTMLElement;
  // M3 WORLD B3/B6: signal readout, objective line, B-NARRATIVE drift-log.
  private readonly signalEl: HTMLElement;
  private readonly objectiveEl: HTMLElement;
  private readonly guidanceEl: HTMLElement;
  private readonly driftEl: HTMLElement;
  // AAA-02: the Verification Ledger panel (the differentiating component).
  private readonly ledgerEl: HTMLElement;
  // ITEM 3: orbit-lines toggle indicator.
  private readonly orbitLinesEl: HTMLElement;

  constructor() {
    const root = document.createElement('div');
    root.id = 'hud';
    root.innerHTML = `
      <div id="hud-instruments">
        <div id="hud-throttle" class="hud-glow hud-instrument">
          <span>THR</span><span class="bar"><b class="blue"></b><i></i></span>
          <span id="hud-speed" class="val">0 M/S</span>
        </div>
        <div id="hud-assist" class="hud-glow hud-instrument on">ASSIST ON</div>
        <div id="hud-pips" class="hud-glow hud-instrument">
          <div class="prow"><span>SYS</span><span class="cells" data-k="sys"></span></div>
          <div class="prow"><span>ENG</span><span class="cells" data-k="eng"></span></div>
          <div class="prow"><span>WEP</span><span class="cells" data-k="wep"></span></div>
        </div>
        <div id="hud-target" class="hud-glow hud-instrument"></div>
        <div id="hud-cruise" class="hud-glow hud-instrument">
          <div class="mode">NORMAL</div>
          <div class="spool"><i></i></div>
          <div class="safe"></div>
        </div>
        <div id="hud-ship" class="hud-glow hud-instrument">
          <div class="name">—</div>
          <div class="row"><span>HULL</span><span class="b"><i id="hud-hull"></i></span></div>
          <div class="row"><span>SHLD</span><span class="b"><i id="hud-shld"></i></span></div>
          <div class="row"><span>FUEL</span><span class="b"><i id="hud-fuel"></i></span></div>
          <div class="row"><span>ENGY</span><span class="b"><i id="hud-engy"></i></span></div>
        </div>
        <div id="hud-dock" class="hud-glow hud-instrument"></div>
        <div id="hud-combat" class="hud-glow hud-instrument hidden"></div>
        <div id="hud-econ" class="hud-glow hud-instrument"></div>
        <div id="hud-tl">
          <div id="hud-tl-instruments">
            <div id="hud-signal" class="hud-glow hud-instrument hidden"></div>
          </div>
          <div id="hud-tl-records">
            <div id="hud-objective" class="hidden"></div>
            <div id="hud-guidance" class="hidden"></div>
            <div id="hud-ledger" class="hidden"></div>
          </div>
        </div>
        <div id="hud-orbits" class="hud-glow hud-instrument hud-orbits-off">ORBITS: OFF</div>
        <div id="hud-scanline" aria-hidden="true"></div>
      </div>
      <div id="hud-records">
        <div id="hud-roll"></div>
        <div id="hud-drift" class="hidden"></div>
      </div>`;
    document.body.appendChild(root);
    this.root = root;
    this.instrumentLayer = root.querySelector('#hud-instruments') as HTMLDivElement;
    this.scanlineEl = root.querySelector('#hud-scanline') as HTMLDivElement;
    this.throttleFill = root.querySelector('#hud-throttle .bar > i') as HTMLElement;
    this.throttleBlue = root.querySelector('#hud-throttle .bar > .blue') as HTMLElement;
    this.speedEl = root.querySelector('#hud-speed') as HTMLElement;
    this.assistEl = root.querySelector('#hud-assist') as HTMLElement;
    this.pipCells = {
      sys: root.querySelector('#hud-pips .cells[data-k="sys"]') as HTMLElement,
      eng: root.querySelector('#hud-pips .cells[data-k="eng"]') as HTMLElement,
      wep: root.querySelector('#hud-pips .cells[data-k="wep"]') as HTMLElement,
    };
    this.targetEl = root.querySelector('#hud-target') as HTMLElement;
    this.cruiseModeEl = root.querySelector('#hud-cruise .mode') as HTMLElement;
    this.spoolFill = root.querySelector('#hud-cruise .spool > i') as HTMLElement;
    this.safeEl = root.querySelector('#hud-cruise .safe') as HTMLElement;
    this.shipNameEl = root.querySelector('#hud-ship .name') as HTMLElement;
    this.hullBar = root.querySelector('#hud-hull') as HTMLElement;
    this.shieldBar = root.querySelector('#hud-shld') as HTMLElement;
    this.fuelBar = root.querySelector('#hud-fuel') as HTMLElement;
    this.energyBar = root.querySelector('#hud-engy') as HTMLElement;
    this.dockEl = root.querySelector('#hud-dock') as HTMLElement;
    this.combatEl = root.querySelector('#hud-combat') as HTMLElement;
    this.rollEl = root.querySelector('#hud-roll') as HTMLElement;
    this.econEl = root.querySelector('#hud-econ') as HTMLElement;
    this.signalEl = root.querySelector('#hud-signal') as HTMLElement;
    this.objectiveEl = root.querySelector('#hud-objective') as HTMLElement;
    this.guidanceEl = root.querySelector('#hud-guidance') as HTMLElement;
    this.driftEl = root.querySelector('#hud-drift') as HTMLElement;
    this.ledgerEl = root.querySelector('#hud-ledger') as HTMLElement;
    this.orbitLinesEl = root.querySelector('#hud-orbits') as HTMLElement;

    this.svg = this.buildSvg();
    this.bracket = this.svg.querySelector('#brk') as SVGRectElement;
    this.arrow = this.svg.querySelector('#arr') as SVGPolygonElement;
    this.prograde = this.svg.querySelector('#prg') as SVGCircleElement;
    this.lockReticle = this.svg.querySelector('#lck') as SVGGElement;
    this.lockHealthBar = this.svg.querySelector('#lhb') as SVGGElement;
    this.leadPip = this.svg.querySelector('#lead-pip') as SVGCircleElement;
    this.dmgDirArrow = this.svg.querySelector('#dmg-dir') as SVGPolygonElement;
    this.hitMarker = this.svg.querySelector('#hitmark') as SVGGElement;
    this.stickGroup = this.svg.querySelector('#stk') as SVGGElement;
    this.stickDot = this.svg.querySelector('#stk-dot') as SVGCircleElement;
    this.offScreenPool = this.buildOffScreenPool(5);
    this.radar = this.buildRadar();
    this.setMode('chase');
  }

  private buildSvg(): SVGSVGElement {
    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('class', 'hud-svg hud-instrument');
    svg.innerHTML = `
      <g stroke="var(--bm-primary)" fill="none" stroke-width="1.5" opacity="0.9">
        <circle id="ret-c" r="10"></circle>
        <line id="ret-h1"></line><line id="ret-h2"></line>
        <line id="ret-v1"></line><line id="ret-v2"></line>
      </g>
      <circle id="prg" r="6" stroke="var(--bm-confirm)" fill="none" stroke-width="1.5"></circle>
      <rect id="brk" stroke="var(--bm-warning)" fill="none" stroke-width="2" width="48" height="48"></rect>
      <polygon id="arr" fill="var(--bm-warning)" points="0,0 -10,-18 10,-18" opacity="0"></polygon>
      <g id="lck" stroke="var(--bm-alert)" fill="none" stroke-width="2" opacity="0">
        <line x1="-28" y1="-28" x2="-28" y2="-16"></line><line x1="-28" y1="-28" x2="-16" y2="-28"></line>
        <line x1="28" y1="-28" x2="28" y2="-16"></line><line x1="28" y1="-28" x2="16" y2="-28"></line>
        <line x1="-28" y1="28" x2="-28" y2="16"></line><line x1="-28" y1="28" x2="-16" y2="28"></line>
        <line x1="28" y1="28" x2="28" y2="16"></line><line x1="28" y1="28" x2="16" y2="28"></line>
      </g>
      <g id="lhb" opacity="0">
        <rect id="lhb-shld-bg" x="-28" y="34" width="56" height="4" fill="rgba(var(--bm-primary-rgb),0.15)" stroke="none"></rect>
        <rect id="lhb-shld-fg" x="-28" y="34" width="56" height="4" fill="var(--bm-primary)" stroke="none"></rect>
        <rect id="lhb-hull-bg" x="-28" y="40" width="56" height="4" fill="rgba(var(--bm-confirm-rgb),0.15)" stroke="none"></rect>
        <rect id="lhb-hull-fg" x="-28" y="40" width="56" height="4" fill="var(--bm-confirm)" stroke="none"></rect>
      </g>
      <circle id="lead-pip" r="4" stroke="var(--bm-alert)" fill="none" stroke-width="1.5" stroke-dasharray="3 3" opacity="0"></circle>
      <polygon id="dmg-dir" fill="var(--bm-alert-hot)" points="0,0 -8,-16 8,-16" opacity="0"></polygon>
      <g id="hitmark" stroke="var(--bm-confirm)" fill="none" stroke-width="2" opacity="0">
        <line x1="-16" y1="-16" x2="-5" y2="-5"></line><line x1="16" y1="-16" x2="5" y2="-5"></line>
        <line x1="-16" y1="16" x2="-5" y2="5"></line><line x1="16" y1="16" x2="5" y2="5"></line>
      </g>
      <g id="stk" opacity="0">
        <circle id="stk-ring" r="40" stroke="var(--bm-primary)" fill="none" stroke-width="1" stroke-opacity="0.35"></circle>
        <circle id="stk-dot" r="3" fill="var(--bm-primary)" stroke="none"></circle>
      </g>`;
    this.instrumentLayer.appendChild(svg);
    return svg;
  }

  private buildRadar(): HTMLDivElement {
    const r = document.createElement('div');
    r.id = 'hud-radar';
    r.className = 'hud-instrument';
    this.instrumentLayer.appendChild(r);
    return r;
  }

  /** H3: pre-allocate pool of off-screen hostile arrows (no per-frame DOM creation). */
  private buildOffScreenPool(count: number): SVGPolygonElement[] {
    const pool: SVGPolygonElement[] = [];
    for (let i = 0; i < count; i++) {
      const p = document.createElementNS(NS, 'polygon') as SVGPolygonElement;
      p.setAttribute('fill', 'var(--bm-alert-hot)');
      p.setAttribute('points', '0,0 -7,-14 7,-14');
      p.setAttribute('opacity', '0');
      this.svg.appendChild(p);
      pool.push(p);
    }
    return pool;
  }

  /** H3: hull+shield bars overlaid just below the lock-reticle bracket. */
  private updateLockHealthBar(f: HudFrame): void {
    if (!f.combatLockScreen) {
      this.lockHealthBar.setAttribute('opacity', '0');
      return;
    }
    const { x, y, hull01, shield01 } = f.combatLockScreen;
    this.lockHealthBar.setAttribute('transform', `translate(${x},${y})`);
    this.lockHealthBar.setAttribute('opacity', '1');
    const shldFg = this.lockHealthBar.querySelector('#lhb-shld-fg') as SVGRectElement;
    const hullFg = this.lockHealthBar.querySelector('#lhb-hull-fg') as SVGRectElement;
    const w = 56;
    shldFg.setAttribute('width', `${Math.round(Math.max(0, Math.min(1, shield01)) * w)}`);
    hullFg.setAttribute('width', `${Math.round(Math.max(0, Math.min(1, hull01)) * w)}`);
  }

  /** H3: small dashed circle at the lead intercept point (aim here). */
  private updateLeadPip(f: HudFrame): void {
    if (!f.combatLeadScreen) {
      this.leadPip.setAttribute('opacity', '0');
      return;
    }
    this.leadPip.setAttribute('cx', `${f.combatLeadScreen.x}`);
    this.leadPip.setAttribute('cy', `${f.combatLeadScreen.y}`);
    this.leadPip.setAttribute('opacity', '0.85');
  }

  /** H3: brief edge arrow pointing toward the damage source; fades out. */
  private updateDmgDir(f: HudFrame): void {
    if (!f.combatDmgDir || f.combatDmgDir.fade <= 0) {
      this.dmgDirArrow.setAttribute('opacity', '0');
      return;
    }
    const { angle, fade } = f.combatDmgDir;
    const cx = this.cx();
    const cy = this.cy();
    const r = Math.min(window.innerWidth, window.innerHeight) / 2 - 72;
    const px = cx + Math.cos(angle) * r;
    const py = cy + Math.sin(angle) * r;
    const deg = (angle * 180) / Math.PI + 90;
    this.dmgDirArrow.setAttribute('transform', `translate(${px},${py}) rotate(${deg})`);
    this.dmgDirArrow.setAttribute('opacity', `${(fade * 0.9).toFixed(2)}`);
  }

  /** M4 CONTROL-FEEL: stick-deflection dot inside a faint guide ring at the
   * reticle. Opacity scales with deflection so a centered stick leaves the
   * reticle clean; hidden entirely when frame.stick is null (docked/menus). */
  private updateStick(f: HudFrame): void {
    const R = 40; // guide-ring radius (px) = full deflection
    if (!f.stick) {
      this.stickGroup.setAttribute('opacity', '0');
      return;
    }
    const mag = Math.min(1, Math.max(Math.abs(f.stick.x), Math.abs(f.stick.y)));
    this.stickGroup.setAttribute('opacity', `${(0.15 + 0.65 * mag).toFixed(2)}`);
    this.stickGroup.setAttribute('transform', `translate(${this.cx()},${this.cy()})`);
    // y is pitch command: positive pitch = nose up = dot above center.
    this.stickDot.setAttribute('cx', `${(f.stick.x * R).toFixed(1)}`);
    this.stickDot.setAttribute('cy', `${(-f.stick.y * R).toFixed(1)}`);
  }

  /** H3: edge-clamped arrows for off-screen enc-* hostiles (pooled, capped at 5). */
  private updateOffScreenHostiles(f: HudFrame): void {
    const cx = this.cx();
    const cy = this.cy();
    const r = Math.min(window.innerWidth, window.innerHeight) / 2 - 56;
    for (let i = 0; i < this.offScreenPool.length; i++) {
      const el = this.offScreenPool[i]!;
      const entry = f.combatOffScreen[i];
      if (!entry) {
        el.setAttribute('opacity', '0');
        continue;
      }
      const px = cx + Math.cos(entry.angle) * r;
      const py = cy + Math.sin(entry.angle) * r;
      const deg = (entry.angle * 180) / Math.PI + 90;
      el.setAttribute('transform', `translate(${px},${py}) rotate(${deg})`);
      el.setAttribute('opacity', '0.75');
    }
  }

  private cx(): number {
    return window.innerWidth / 2;
  }
  private cy(): number {
    return window.innerHeight / 2;
  }

  private updateReticle(): void {
    const x = this.cx();
    const y = this.cy();
    const set = (id: string, x1: number, y1: number, x2: number, y2: number): void => {
      const l = this.svg.querySelector(`#${id}`) as SVGLineElement;
      l.setAttribute('x1', `${x1}`);
      l.setAttribute('y1', `${y1}`);
      l.setAttribute('x2', `${x2}`);
      l.setAttribute('y2', `${y2}`);
    };
    const c = this.svg.querySelector('#ret-c') as SVGCircleElement;
    c.setAttribute('cx', `${x}`);
    c.setAttribute('cy', `${y}`);
    set('ret-h1', x - 22, y, x - 13, y);
    set('ret-h2', x + 13, y, x + 22, y);
    set('ret-v1', x, y - 22, x, y - 13);
    set('ret-v2', x, y + 13, x, y + 22);
  }

  private updateHitMarker(f: HudFrame): void {
    const opacity = Math.max(0, Math.min(1, f.combatHitFlash / 0.35));
    if (opacity <= 0) {
      this.hitMarker.setAttribute('opacity', '0');
      return;
    }
    const scale = 1 + (1 - opacity) * 0.25;
    this.hitMarker.setAttribute('opacity', opacity.toFixed(2));
    this.hitMarker.setAttribute('transform', `translate(${this.cx()},${this.cy()}) scale(${scale.toFixed(2)})`);
  }

  private updateBracket(f: HudFrame): void {
    if (!f.targetScreen) {
      this.bracket.setAttribute('opacity', '0');
      this.arrow.setAttribute('opacity', '0');
      return;
    }
    const w = window.innerWidth;
    const h = window.innerHeight;
    const margin = 40;
    const onScreen =
      !f.targetScreen.behind &&
      f.targetScreen.x > margin &&
      f.targetScreen.x < w - margin &&
      f.targetScreen.y > margin &&
      f.targetScreen.y < h - margin;
    if (onScreen) {
      this.bracket.setAttribute('opacity', '1');
      this.bracket.setAttribute('x', `${f.targetScreen.x - 24}`);
      this.bracket.setAttribute('y', `${f.targetScreen.y - 24}`);
      this.arrow.setAttribute('opacity', '0');
    } else {
      this.bracket.setAttribute('opacity', '0');
      const cx = this.cx();
      const cy = this.cy();
      let dx = f.targetScreen.x - cx;
      let dy = f.targetScreen.y - cy;
      if (f.targetScreen.behind) {
        dx = -dx;
        dy = -dy;
      }
      const ang = Math.atan2(dy, dx);
      const px = cx + Math.cos(ang) * (Math.min(w, h) / 2 - 60);
      const py = cy + Math.sin(ang) * (Math.min(w, h) / 2 - 60);
      this.arrow.setAttribute('opacity', '1');
      this.arrow.setAttribute(
        'transform',
        `translate(${px},${py}) rotate(${(ang * 180) / Math.PI + 90})`,
      );
    }
  }

  private updateProgradeMarker(f: HudFrame): void {
    if (!f.progradeScreen || f.speedKmS < 0.001) {
      this.prograde.setAttribute('opacity', '0');
      return;
    }
    this.prograde.setAttribute('opacity', '0.9');
    this.prograde.setAttribute('cx', `${f.progradeScreen.x}`);
    this.prograde.setAttribute('cy', `${f.progradeScreen.y}`);
  }

  update(f: HudFrame): void {
    // ITEM 3: vertical throttle — the fill grows bottom→top (height, not width).
    // ITEM 3 ADJUST: colour-code by sign — forward (cyan) vs reverse (amber).
    this.throttleFill.style.height = `${Math.abs(f.throttle) * 100}%`;
    this.throttleFill.classList.toggle('reverse', f.throttle < 0);
    this.updateBlueZone(f);
    this.speedEl.textContent = fmtSpeed(f.speedKmS).toUpperCase();

    this.updateAssistPill(f);
    this.updatePips(f);

    if (f.target) {
      this.targetEl.innerHTML =
        `<div>${f.target.name.toUpperCase()}</div>` +
        `<div><span class="val">${fmtDistance(f.target.distanceKm)}</span> · ` +
        `<span class="val">${fmtClosing(f.target.closingSpeed)}</span></div>`;
    } else {
      this.targetEl.textContent = 'NO TARGET';
    }

    this.cruiseModeEl.textContent = f.cruise === 'NORMAL' ? 'NORMAL' : 'SUPERCRUISE';
    this.cruiseModeEl.className = `mode ${f.cruise === 'NORMAL' ? '' : 'sc'}`;
    this.spoolFill.style.width = `${f.spoolPercent}%`;
    this.safeEl.textContent = f.canSafeDisengage ? 'APPROACH — DROP TO NORMAL [X]' : '';

    // M2 P1: ship-state bars (clamped 0..1 each).
    this.shipNameEl.textContent = f.shipName.toUpperCase();
    this.hullBar.style.width = `${Math.round(Math.max(0, Math.min(1, f.hull01)) * 100)}%`;
    this.shieldBar.style.width = `${Math.round(Math.max(0, Math.min(1, f.shield01)) * 100)}%`;
    this.fuelBar.style.width = `${Math.round(Math.max(0, Math.min(1, f.fuel01)) * 100)}%`;
    this.energyBar.style.width = `${Math.round(Math.max(0, Math.min(1, f.energy01)) * 100)}%`;

    this.updateReticle();
    this.updateHitMarker(f);
    this.updateStick(f); // M4: commanded-turn indicator
    this.updateBracket(f);
    this.updateLockReticle(f);
    this.updateLockHealthBar(f);
    this.updateLeadPip(f);
    this.updateDmgDir(f);
    this.updateOffScreenHostiles(f);
    this.updateProgradeMarker(f);
    this.updateDock(f);
    this.updateCombat(f);
    this.updateRoll(f);
    this.updateEcon(f);
    this.updateSignal(f);
    this.updateObjective(f);
    this.updateGuidance(f);
    this.updateDrift(f);
    this.updateLedger(f);
    this.updateOrbitLines(f);
  }

  /** ITEM 3: orbit-lines toggle indicator — cyan when ON, dimmed when OFF. */
  private updateOrbitLines(f: HudFrame): void {
    if (f.orbitLinesOn) {
      this.orbitLinesEl.textContent = 'ORBITS: ON';
      this.orbitLinesEl.className = 'hud-glow hud-orbits-on';
    } else {
      this.orbitLinesEl.textContent = 'ORBITS: OFF';
      this.orbitLinesEl.className = 'hud-glow hud-orbits-off';
    }
  }

  /** M3 WORLD B3: signal readout — label, distance, threat, DROP/SCOOP prompts. */
  private updateSignal(f: HudFrame): void {
    if (!f.signalLabel) {
      this.signalEl.className = 'hud-glow hidden';
      return;
    }
    const threat = f.signalThreat > 0 ? `<span class="threat">THREAT ${f.signalThreat}</span>` : 'SAFE';
    const prompt = f.signalPrompt ? `<div class="prompt">${f.signalPrompt}</div>` : '';
    const scoop = f.scoopPrompt ? `<div class="prompt scoop">${f.scoopPrompt}</div>` : '';
    const verifyClass =
      f.verifyStatus === 'CONTRADICTION' ? 'prompt verify contradiction' : 'prompt verify';
    const verify = f.verifyPrompt ? `<div class="${verifyClass}">${f.verifyPrompt}</div>` : '';
    this.signalEl.className = `hud-glow ${f.signalThreat >= 3 ? 'hot' : ''}`;
    this.signalEl.innerHTML =
      `<div>${f.signalLabel.toUpperCase()}</div>` +
      `<div><span class="val">${fmtDistance(f.signalDistanceKm)}</span> · ${threat}</div>${prompt}${scoop}${verify}`;
  }

  /** M3 WORLD B6: active objective (Tasks) one-liner — hidden when none.
   * Toggles `hidden` so the #hud-tl flex column collapses the row and its gap
   * (display:none) instead of leaving an empty slot when no objective is set. */
  private updateObjective(f: HudFrame): void {
    if (!f.objectiveText) {
      this.objectiveEl.textContent = '';
      this.objectiveEl.className = 'hidden';
      return;
    }
    this.objectiveEl.textContent = f.objectiveText;
    this.objectiveEl.className = '';
  }

  /** FIX 3: terse, non-glowing guidance for the verify verb and its outcome. */
  private updateGuidance(f: HudFrame): void {
    if (!f.verifyGuidance) {
      this.guidanceEl.textContent = '';
      this.guidanceEl.className = 'hidden';
      return;
    }
    this.guidanceEl.textContent = f.verifyGuidance;
    this.guidanceEl.className = '';
  }

  /** B-NARRATIVE: readable derelict drift-log on drop-in (sombre, dwell to read). */
  private updateDrift(f: HudFrame): void {
    if (!f.driftLog) {
      this.driftEl.className = 'hidden'; // AAA-21: mourning prose never glows
      return;
    }
    this.driftEl.className = '';
    this.driftEl.textContent = f.driftLog;
  }

  /** AAA-02: the Verification Ledger — the signature component. Each carved name is
   * written glyph-by-glyph; its status (VERIFIED cyan / CONTRADICTION red) appears
   * only once the name is fully written. Never celebratory. */
  private updateLedger(f: HudFrame): void {
    if (f.ledger.length === 0) {
      this.ledgerEl.className = 'hidden'; // AAA-21: the record never glows
      return;
    }
    this.ledgerEl.className = '';
    this.ledgerEl.innerHTML = f.ledger
      .map((e) => {
        const shown = e.name.slice(0, Math.floor(e.revealChars));
        const done = e.revealChars >= e.name.length;
        const status = done ? `<span class="st ${e.status.toLowerCase()}">${this.statusStamp(e.status)}</span>` : '';
        return `<div class="lrow"><span class="nm">${shown}</span>${status}</div>`;
      })
      .join('');
  }

  private statusStamp(status: VerifyStatus): string {
    return status === 'CONTRADICTION' ? '✕ CONTRADICTION' : '✓ TRUTH';
  }

  /**
   * FLIGHT-FEEL A2: assist pill — ASSIST ON (both), ASSIST OFF (both off), or
   * ROT OFF (linear on, rotational off).
   */
  private updateAssistPill(f: HudFrame): void {
    if (f.assist && f.rotAssist) {
      this.assistEl.textContent = 'ASSIST ON';
      this.assistEl.className = 'hud-glow on';
    } else if (!f.assist && !f.rotAssist) {
      this.assistEl.textContent = 'ASSIST OFF';
      this.assistEl.className = 'hud-glow off';
    } else {
      this.assistEl.textContent = f.rotAssist ? 'ASSIST OFF' : 'ROT OFF';
      this.assistEl.className = 'hud-glow off';
    }
  }

  /** FLIGHT-FEEL A4: render SYS/ENG/WEP pip cells (filled = allocated). */
  private updatePips(f: HudFrame): void {
    const cells = (n: number): string => {
      let out = '';
      for (let i = 0; i < 6; i++) out += `<u class="${i < n ? 'on' : ''}"></u>`;
      return out;
    };
    this.pipCells.sys.innerHTML = cells(f.pips.sys);
    this.pipCells.eng.innerHTML = cells(f.pips.eng);
    this.pipCells.wep.innerHTML = cells(f.pips.wep);
  }

  /** FLIGHT-FEEL A5: position the blue band on the throttle bar; brighten in-zone.
   * ITEM 3: the bar is now vertical, so the optimal band is a horizontal slice
   * positioned from the BOTTOM (bottom/height) rather than the left (left/width). */
  private updateBlueZone(f: HudFrame): void {
    const lo = Math.max(0, Math.min(1, f.blueLo));
    const hi = Math.max(lo, Math.min(1, f.blueHi));
    this.throttleBlue.style.bottom = `${lo * 100}%`;
    this.throttleBlue.style.height = `${(hi - lo) * 100}%`;
    this.throttleBlue.className = `blue ${f.inBlueZone ? 'active' : ''}`;
  }

  /** M2 P5 / AAA-05: ledger balance + manifest line, framed as a Nameswriter's
   * audit terminal (LEDGER/MANIFEST) rather than a trader's CR/HOLD. Label-only;
   * the underlying credits/cargo fields are unchanged. */
  private updateEcon(f: HudFrame): void {
    const mission = f.missionText ? `<div class="prompt">${f.missionText}</div>` : '';
    this.econEl.innerHTML =
      `<div>LEDGER <span class="val">${f.credits}</span> · ` +
      `MANIFEST <span class="val">${f.cargoUsed}/${f.cargoCapacity}</span></div>${mission}`;
  }

  /** M2 P4 / FIX 3: locked-target HP panel — hidden unless an NPC is in the aim cone.
   * combatHitFlash: briefly tints the panel green on a confirmed player hit.
   * combatDmgFlash: briefly tints the panel red on incoming hostile hit. */
  private updateCombat(f: HudFrame): void {
    if (!f.combatName) {
      this.combatEl.className = 'hud-glow hidden';
      return;
    }
    let cls = 'hud-glow';
    if (f.combatDmgFlash > 0) cls += ' combat-dmg';
    else if (f.combatHitFlash > 0) cls += ' combat-hit';
    this.combatEl.className = cls;
    this.combatEl.innerHTML =
      `<div class="name">${f.combatName}</div>` +
      `<div class="row"><span>SHLD</span><span class="b"><i style="width:${this.pct(f.combatShield01)}"></i></span></div>` +
      `<div class="row"><span>HULL</span><span class="b"><i style="width:${this.pct(f.combatHull01)}"></i></span></div>`;
  }

  /** M2 P4: the Roll — sombre, named, non-celebratory, non-skippable (fades out). */
  private updateRoll(f: HudFrame): void {
    this.rollEl.innerHTML = f.rollNotices
      .map((r) => `<div class="lost" style="opacity:${this.rollOpacity(r.age)}">LOST: ${r.name.toUpperCase()}</div>`)
      .join('');
  }

  /** Clamp a 0..1 fraction to a CSS width percent. */
  private pct(v: number): string {
    return `${Math.round(Math.max(0, Math.min(1, v)) * 100)}%`;
  }

  /** Roll entry opacity from age — fades to 0 over the hold window. */
  private rollOpacity(age: number): number {
    return Math.max(0, 1 - age / ROLL_HOLD_SEC);
  }

  /** M2 P2: docking guidance panel — hidden unless a station is in range. */
  private updateDock(f: HudFrame): void {
    if (!f.dockStationName) {
      this.dockEl.textContent = '';
      this.dockEl.className = 'hud-glow';
      return;
    }
    this.dockEl.innerHTML =
      `<div>${f.dockStationName.toUpperCase()}</div>` +
      `<div><span class="val">${fmtDistance(f.dockDistanceKm)}</span> · ` +
      `<span class="val">${fmtClosing(-f.dockSpeedKmS)}</span></div>` +
      `<div class="prompt">${f.dockPromptText}</div>`;
    const lit = f.dockPhase === 'DOCKED' || f.dockPhase === 'CLEARED' || f.dockPhase === 'APPROACH';
    this.dockEl.className = `hud-glow ${lit ? 'cleared' : ''}`;
  }

  /** Project a render-space (camera-relative) world point to screen px. */
  static project(
    pos: THREE.Vector3,
    camera: THREE.Camera,
  ): { x: number; y: number; behind: boolean } {
    const v = pos.clone().project(camera);
    return {
      x: ((v.x + 1) / 2) * window.innerWidth,
      y: ((1 - v.y) / 2) * window.innerHeight,
      behind: v.z > 1,
    };
  }

  /** FIX 3: combat lock reticle — corner brackets at the locked target's screen pos.
   * Hidden when no lock or target is off-screen. Tints red during dmg flash. */
  private updateLockReticle(f: HudFrame): void {
    if (!f.combatName || !f.targetScreen || f.targetScreen.behind) {
      this.lockReticle.setAttribute('opacity', '0');
      return;
    }
    const w = window.innerWidth;
    const h = window.innerHeight;
    const { x, y } = f.targetScreen;
    if (x < 0 || x > w || y < 0 || y > h) {
      this.lockReticle.setAttribute('opacity', '0');
      return;
    }
    this.lockReticle.setAttribute('transform', `translate(${x},${y})`);
    this.lockReticle.setAttribute('opacity', '1');
    const col =
      f.combatDmgFlash > 0
        ? 'var(--bm-alert-hot)'
        : f.combatHitFlash > 0
          ? 'var(--bm-confirm)'
          : 'var(--bm-alert)';
    this.lockReticle.setAttribute('stroke', col);
  }

  dispose(): void {
    document.body.classList.remove('cockpit-hud');
    this.root.classList.remove('cockpit-hud');
    this.root.remove();
  }

  setMode(mode: CameraMode): void {
    const cockpit = mode === 'cockpit';
    document.body.classList.toggle('cockpit-hud', cockpit);
    this.root.classList.toggle('cockpit-hud', cockpit);
  }

  setVisible(visible: boolean): void {
    const value = visible ? '' : 'none';
    this.root.style.display = value;
    this.svg.style.display = value;
    this.radar.style.display = value;
    this.scanlineEl.style.display = value;
  }
}
